







































import { computed, ref, defineComponent, PropType, toRefs, onMounted } from '@vue/composition-api';
import Stamp from '@/components/stamp-module/stamp-item.vue';
import TaskBadage from './badage.vue';
import TaskProgress from './progress.vue';
import TaskJumpBtn from './jump-btn.vue';
import Button from '@/components/common/button.vue';
import { TaskItem, TaskStatus } from '@/types/task';
import { i18n } from '@/i18n';
import { formatPercent } from '@/utils';
import { toCurrency } from '@tencent/wphk-base-utils/lib/amount';
import { useTaskJumpBtn } from '@/hooks/use-task-jump-btn';
import { STAMP_ICON_CONF } from '@/config';
import { measureTextRows } from '@/utils/text-rows';
import { language } from '@tencent/ppd-base-libs';
import { parseCurrencyAria } from '@tencent/wphk-act-base-ui';

export default defineComponent({
  name: 'TaskItem',
  props: {
    /** 任务信息 */
    taskInfo: {
      type: Object as PropType<TaskItem>,
      required: true,
    },
  },
  components: {
    Stamp,
    TaskProgress,
    TaskJumpBtn,
    TaskBadage,
    Button,
  },
  setup(props, { emit }) {
    const { taskInfo } = toRefs(props);
    const { isShowJump, beforeJump } = toRefs(useTaskJumpBtn(props.taskInfo));
    /** 是否展示进度 */
    const isShowProgress = computed(() => {
      const { maxTimesPerStage } = taskInfo.value;
      const isMulti = maxTimesPerStage > 1;
      // 能完成多次则一直展示进度
      if (isMulti) return true;
      // 只能完成一次则当展示按钮时不展示
      return !isShowJump.value;
    });
    /** 任务是否未开始 */
    const isTaskNotStart = computed(() => taskInfo.value.status === TaskStatus.notStart);
    /** 是否展示任务badage */
    const isShowBadage = computed(() => taskInfo.value.type === 'limit' || taskInfo.value.status === TaskStatus.soldOut);
    /** 限额任务的描述 */
    const stampLimitTaskDesc = computed(() => {
      const { status, taskSendLimit, taskSendCnt, taskTotalCnt } = taskInfo.value;
      switch (status) {
        case TaskStatus.notStart:
        case TaskStatus.outdate:
          return i18n('{{taskTotalCnt}}個印花限量派，派完即止', { taskTotalCnt: toCurrency(String(taskTotalCnt), 0, false) });
        case TaskStatus.soldOut:
          return i18n('{{taskTotalCnt}}個印花已派完', { taskTotalCnt: toCurrency(String(taskTotalCnt), 0, false) });
        case TaskStatus.inProgress: {
          const percentage = 100 - Number(formatPercent(taskSendCnt, taskSendLimit));
          return i18n('印花已派<span class="task-desc-orange">{{percentage}}%</span>，派完即止', { percentage });
        }
        default:
          return '';
      }
    });
    // 每月最多个数
    const monthCount = computed(() => {
      const { maxTimesPerStage, prizeCountPerTime } = taskInfo.value;
      return maxTimesPerStage * prizeCountPerTime;
    });
    /** 任务列表里的描述 */
    const taskDesc = computed(() => {
      const { maxTimesPerDay, prizeCountPerTime, taskSendLimit } = taskInfo.value;
      /** 如果是限额任务，则展示印花派发额度相关信息 */
      if (taskSendLimit) {
        return stampLimitTaskDesc.value;
      }
      if (maxTimesPerDay) {
        const dayCount = maxTimesPerDay * prizeCountPerTime;
        return i18n('每日{{dayCount}}個，本月最多{{monthCount}}', {
          dayCount,
          monthCount: `<span class="task-stamp"><img src="${STAMP_ICON_CONF.default}" class="task-stamp-icon" />x${monthCount.value}</span>`,
        });
      }
      return i18n('本月最多{{monthCount}}', {
        monthCount: `<span class="task-stamp"><img src="${STAMP_ICON_CONF.default}" class="task-stamp-icon" />x${monthCount.value}</span>`,
      });
    });
    const taskDescAriaLabel = computed(() =>  parseCurrencyAria(parseTaskDescAria(taskDesc.value), true));
    /** 任务详情弹窗里的描述 */
    const taskDetailDesc = computed(() => {
      const { maxTimesPerStage, maxTimesPerDay, prizeCountPerTime } = taskInfo.value;
      const monthCount = maxTimesPerStage * prizeCountPerTime;
      if (maxTimesPerDay) {
        const dayCount = maxTimesPerDay * prizeCountPerTime;
        return i18n('每天{{dayCount}}個，本月最多可儲{{monthCount}}個', { dayCount, monthCount });
      }
      return i18n('本月最多可儲{{monthCount}}個', { monthCount });
    });
    /** 时间信息 */
    const dateInfo = computed(() => {
      const { startTime, endTime } = taskInfo.value;
      return [startTime, endTime] as [Date, Date];
    });

    /** 打开详情弹窗 */
    const onClickTaskInfo = () => {
      emit('onClickTaskInfo', taskInfo.value.id);
    };

    const taskName = ref(taskInfo.value.name);
    const isLangEn = language.isEn() as boolean;
    // const name = 'This is the longest is the largest length of a text so that we can test the measure text rows.';
    // const isLangEn = true;

    function parseTaskDescAria(desc: string) {
      const pn = /<img.+\/>x(\d+)/;
      if (!desc) return '';
      return desc.replace(pn, (val) => {
        const count = parseInt(val.replace(/<img.+\/>x/, ''), 10);
        const text = i18n('{{count}}个印花', { count }) as string;
        return count <= 1 && isLangEn ? text.replace('e-stamps', 'e-stamp') : text;
      });
    }

    onMounted(() => {
      if (!isLangEn) {
        return;
      }
      /**
       * 对于英文语言下，任务名称可以扩展到3行展示，如果超过3行则使用省略号
       */
      const target = document.querySelector('.task-title') as HTMLDivElement;
      const rows = measureTextRows(taskName.value, target);
      if (rows >= 3) {
        taskName.value = `${taskName.value.substring(0, 60)}...`;
      }
    });

    return {
      isShowProgress,
      isShowBadage,
      isTaskNotStart,
      taskDesc,
      taskDescAriaLabel,
      taskDetailDesc,
      dateInfo,
      onClickTaskInfo,
      isShowJump,
      beforeJump,
      taskName,
      isLangEn,
    };
  },
});
